/**
 * Configuración de destinatarios de alertas — `/api/notification-configs`.
 *
 * Cada config matchea por (clientSlug, urgencyLevels). Si urgencyLevels está
 * vacío, dispara para todas las severidades. El backend acepta múltiples
 * configs por cliente (ej: mail al gerente + WA al director).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiDelete, apiGet, apiPatch, apiPost } from "@/lib/api";
import type { ClientSlug } from "@/types";

export type NotificationChannel =
  | "mail"
  | "whatsapp"
  | "sms"
  | "slack"
  | "discord"
  | "call";

export type UrgencyLevel = "critical" | "high" | "medium" | "low";

export interface NotificationConfig {
  id: string;
  clientSlug: ClientSlug;
  channel: NotificationChannel;
  destination: string;
  urgencyLevels: UrgencyLevel[];
  escalationMinutes: number | null;
  createdAt: string;
  updatedAt: string;
}

export function useNotificationConfigs(clientSlug: ClientSlug | undefined) {
  return useQuery<NotificationConfig[]>({
    queryKey: ["notification-configs", clientSlug],
    enabled: !!clientSlug,
    queryFn: async () => {
      if (!clientSlug) return [];
      const qs = new URLSearchParams({ clientSlug });
      const res = await apiGet<{ items: NotificationConfig[] }>(
        `/notification-configs?${qs.toString()}`,
      );
      return res.items;
    },
  });
}

interface CreateInput {
  clientSlug: ClientSlug;
  channel: NotificationChannel;
  destination: string;
  urgencyLevels: UrgencyLevel[];
  escalationMinutes?: number;
}

export function useCreateNotificationConfig() {
  const qc = useQueryClient();
  return useMutation<NotificationConfig, Error, CreateInput>({
    mutationFn: async (input) => {
      const res = await apiPost<{ ok: boolean; config: NotificationConfig }>(
        "/notification-configs",
        input,
      );
      return res.config;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notification-configs"] });
      toast.success("Destinatario agregado");
    },
    onError: (err) => {
      toast.error(`No se pudo agregar: ${err.message}`);
    },
  });
}

export function useUpdateNotificationConfig() {
  const qc = useQueryClient();
  return useMutation<
    NotificationConfig,
    Error,
    { id: string; patch: Partial<CreateInput> }
  >({
    mutationFn: async ({ id, patch }) => {
      const res = await apiPatch<{ ok: boolean; config: NotificationConfig }>(
        `/notification-configs/${id}`,
        patch,
      );
      return res.config;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notification-configs"] });
      toast.success("Destinatario actualizado");
    },
    onError: (err) => {
      toast.error(`No se pudo actualizar: ${err.message}`);
    },
  });
}

export function useDeleteNotificationConfig() {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: string }>({
    mutationFn: async ({ id }) => {
      await apiDelete<{ ok: boolean }>(`/notification-configs/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["notification-configs"] });
      toast.success("Destinatario eliminado");
    },
    onError: (err) => {
      toast.error(`No se pudo eliminar: ${err.message}`);
    },
  });
}
